import { fetchBets } from './service';
import { AxiosError } from 'axios';
import { GenericError } from '@/models/generic-error';
import { UseBetsQueryKeyProps } from './props';
import { InfiniteData, useInfiniteQuery } from '@tanstack/react-query';
import { BetsSortAndFilterQuery } from '@/services/dto/bet/me/bets-sort-and-filter-query.query';
import { BetsQueryKeys } from '@/api/bets/queryKey';
import { PaginatedResponse } from '@starsoft/common/models';
import { DetailedBet } from '@/models/detailed-bet';
import { useMemo } from 'react';
import { useInfiniteQueryCallbacks } from '@starsoft/common/hooks';

export function useBets(options?: BetsSortAndFilterQuery) {
  const filter: BetsSortAndFilterQuery = {
    sortOrder: options?.sortOrder,
    aggregatorId: options?.aggregatorId,
    gameId: options?.gameId,
    limit: options?.limit ?? 15,
  };
  const queryKey: UseBetsQueryKeyProps = [BetsQueryKeys.Primary, filter];

  const { getNextPageParam, getPreviousPageParam } =
    useInfiniteQueryCallbacks();

  const { data, isPending, fetchStatus, error, refetch, isFetching, ...rest } =
    useInfiniteQuery<
      PaginatedResponse<DetailedBet>,
      AxiosError<GenericError>,
      InfiniteData<PaginatedResponse<DetailedBet>>,
      UseBetsQueryKeyProps
    >({
      queryFn: fetchBets,
      queryKey,
      staleTime: 5 * 1000,
      structuralSharing: false,
      refetchOnWindowFocus: false,
      getNextPageParam,
      getPreviousPageParam,
      initialPageParam: 1,
    });

  const bets = useMemo(() => data?.pages.flatMap(page => page.data), [data]);

  return {
    isPending: (isPending || isFetching) && fetchStatus !== 'idle',
    error,
    refetch,
    data,
    bets,
    isFetching,
    ...rest,
  };
}
